import {
  VoucherApplyResponse,
  VoucherEligibilityRequest,
  VoucherEligibilityResponse,
  VoucherGetByCodeRequest,
  VoucherGetByCodeResponse,
  VoucherGetEligibilitiesRequest,
  VoucherGetEligibilitiesResponse,
  VoucherValidateRequest,
  VoucherValidateResponse,
} from '@diamond/shared/types';
import { apiInstance, buyerCaptureException } from '@diamond/shared/utils';
import { isAxiosError } from 'axios';

export const getVoucherList = async (
  token: string,
  payload: VoucherEligibilityRequest
): Promise<VoucherEligibilityResponse> => {
  try {
    const { data } = await apiInstance(token).post<VoucherEligibilityResponse>(
      '/voucher/eligibility',
      payload
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('VoucherList', 'get voucher list error');
        scope.setUser({
          ...user,
          additional: { warehouse: payload.warehouse_id },
        });
        scope.setLevel('error');
      });
    }
    throw new Error('Error fetching voucher list');
  }
};

export type ProductVoucherEligibilityListRequest = Omit<
  VoucherGetEligibilitiesRequest,
  'page' | 'size'
> &
  Partial<Pick<VoucherGetEligibilitiesRequest, 'page' | 'size'>>;

export const getProductVoucherEligibilityList = async (
  token: string,
  payload: ProductVoucherEligibilityListRequest
): Promise<VoucherGetEligibilitiesResponse> => {
  const page = payload.page ?? 1;
  const size = payload.size ?? 20;

  try {
    const { data } = await apiInstance(token).get<VoucherGetEligibilitiesResponse>(
      '/voucher/eligibilities',
      {
        params: {
          item_code: payload.item_code,
          warehouse: payload.warehouse,
          page,
          size,
        },
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag(
          'ProductVoucherEligibility',
          'get product voucher eligibility error'
        );
        scope.setUser({
          ...user,
          additional: {
            itemCode: payload.item_code,
            warehouse: payload.warehouse,
          },
        });
        scope.setLevel('error');
      });
    }

    throw new Error('Error fetching product voucher eligibility');
  }
};

export const getVoucherByCode = async (
  token: string,
  payload: VoucherGetByCodeRequest
): Promise<VoucherGetByCodeResponse> => {
  try {
    const { data } = await apiInstance(token).post<VoucherGetByCodeResponse>(
      '/voucher/get-by-code',
      payload
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('VoucherByCode', 'get voucher by code error');
        scope.setUser({
          ...user,
          additional: {
            code: payload.voucher_code,
            warehouse: payload.warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

/**
 * Fetch voucher details by code for display purposes only.
 * This is a simplified version that doesn't require cart items.
 * Use this for voucher detail pages where we only need to display voucher information.
 */
export const getVoucherDetailByCode = async (
  token: string,
  voucherCode: string,
  warehouse: string
): Promise<VoucherGetByCodeResponse> => {
  try {
    const { data } = await apiInstance(token).post<VoucherGetByCodeResponse>(
      '/voucher/get-by-code',
      {
        voucher_code: voucherCode.trim().toUpperCase(),
        warehouse,
        cart_total_amount: 0,
        items: [],
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('VoucherDetail', 'get voucher detail error');
        scope.setUser({
          ...user,
          additional: {
            code: voucherCode,
            warehouse,
          },
        });
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

export const applyVoucherCode = async (
  token: string,
  code: string,
  warehouse: string
): Promise<VoucherApplyResponse> => {
  try {
    const { data } = await apiInstance(token).post<VoucherApplyResponse>(
      '/voucher/apply',
      {
        code,
        warehouse,
      }
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('VoucherApply', 'apply voucher error');
        scope.setUser({
          ...user,
          additional: { code, warehouse },
        });
        scope.setLevel('error');
      });
    }
    throw error;
  }
};

/**
 * Validate vouchers before order submission
 */
export const validateVouchers = async (
  token: string,
  payload: VoucherValidateRequest
): Promise<VoucherValidateResponse> => {
  try {
    const { data } = await apiInstance(token).post<VoucherValidateResponse>(
      '/voucher/validate',
      payload
    );
    return data;
  } catch (error) {
    if (isAxiosError(error)) {
      buyerCaptureException(error.response?.data, (scope, user) => {
        scope.setTag('VoucherValidate', 'validate voucher error');
        scope.setUser({
          ...user,
          additional: {
            vouchers: payload.vouchers,
            warehouse: payload.warehouse_id,
            orderMetaId: payload.order_meta_id,
          },
        });
        scope.setLevel('error');
      });
    }
    throw error;
  }
};
